// src/components/common/LoadingState.tsx
import React from 'react';
import {
  Box,
  CircularProgress,
  LinearProgress,
  Typography,
  Paper,
  Skeleton,
  useTheme,
} from '@mui/material';
import { useTranslate } from 'react-admin';

export interface LoadingStateProps {
  /** Tipo de loading a mostrar */
  variant?: 'circular' | 'linear' | 'skeleton' | 'page';
  /** Mensaje personalizado */
  message?: string;
  /** Tamaño del indicador circular */
  size?: number;
  /** Altura mínima del contenedor */
  minHeight?: number | string;
  /** Número de líneas de skeleton */
  skeletonLines?: number;
  /** Si debe ocupar toda la pantalla */
  fullScreen?: boolean;
  /** Color del indicador */
  color?: 'primary' | 'secondary' | 'inherit';
}

/**
 * LoadingState - Componente reutilizable para estados de carga
 * 
 * Características:
 * - Múltiples variantes visuales (circular, linear, skeleton, page)
 * - Mensaje personalizable
 * - Opción de pantalla completa
 * - Diseño consistente
 * - Soporte para traducciones
 */
export const LoadingState: React.FC<LoadingStateProps> = ({
  variant = 'circular',
  message,
  size = 40,
  minHeight = 200,
  skeletonLines = 3,
  fullScreen = false,
  color = 'primary',
}) => {
  const translate = useTranslate();
  const theme = useTheme();
  
  const defaultMessage = translate('ra.message.loading', 'Loading...');

  const containerSx = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: fullScreen ? '100vh' : minHeight,
    p: fullScreen ? 4 : 3,
    textAlign: 'center',
    ...(fullScreen && {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: theme.palette.background.default,
      zIndex: theme.zIndex.modal,
    }),
  };

  switch (variant) {
    case 'linear':
      return (
        <Box sx={containerSx}>
          <Box sx={{ width: '100%', maxWidth: 400, mb: 2 }}>
            <LinearProgress color={color} />
          </Box>
          {(message || !fullScreen) && (
            <Typography variant="body2" color="text.secondary">
              {message || defaultMessage}
            </Typography>
          )}
        </Box>
      );

    case 'skeleton':
      return (
        <Paper elevation={fullScreen ? 0 : 1} sx={{ p: 3 }}>
          <Box sx={{ width: '100%' }}>
            {Array.from({ length: skeletonLines }).map((_, index) => (
              <Skeleton
                key={index}
                variant="text"
                height={40}
                sx={{
                  mb: 1,
                  width: index === skeletonLines - 1 ? '60%' : '100%',
                }}
              />
            ))}
          </Box>
        </Paper>
      );

    case 'page':
      return (
        <Box sx={containerSx}>
          <CircularProgress color={color} size={size} sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {message || defaultMessage}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {translate('ra.message.loading_description', 'Please wait a moment...')}
          </Typography>
        </Box>
      );

    case 'circular':
    default:
      return (
        <Box sx={containerSx}>
          <CircularProgress color={color} size={size} />
          {(message || !fullScreen) && (
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ mt: 2 }}
            >
              {message || defaultMessage}
            </Typography>
          )}
        </Box>
      );
  }
};

export default LoadingState;